import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import AppFooter from '../components/AppFooter';
import { ArrowLeft, GraduationCap, Plus, Trash2, Pencil, Check, X } from 'lucide-react';

interface Grade {
  id: string;
  subject: string;
  grade: string;
  weight: string;
  date: string | null;
  notes: string | null;
  category: string | null;
}
interface Subject { code: string; display_name: string | null; }
interface AveragesResponse {
  per_subject: { subject: string; average: number | null; total_weight: number; count: number }[];
  overall: { average: number | null; count: number };
}

// Typische Kategorien nach bayerischem Schulalltag
const CATEGORIES: { key: string; label: string; defaultWeight: number }[] = [
  { key: 'schulaufgabe', label: 'Schulaufgabe', defaultWeight: 2 },
  { key: 'stegreifaufgabe', label: 'Stegreifaufgabe (Ex)', defaultWeight: 1 },
  { key: 'muendlich', label: 'Mündlich', defaultWeight: 1 },
  { key: 'kurzarbeit', label: 'Kurzarbeit', defaultWeight: 1.5 },
  { key: 'referat', label: 'Referat', defaultWeight: 1 },
  { key: 'projekt', label: 'Projekt / Hausarbeit', defaultWeight: 1 },
  { key: 'sonstiges', label: 'Sonstiges', defaultWeight: 1 },
];

function gradeColor(g: number): string {
  if (g <= 1.5) return 'text-green-400';
  if (g <= 2.5) return 'text-lime-400';
  if (g <= 3.5) return 'text-yellow-400';
  if (g <= 4.5) return 'text-orange-400';
  return 'text-red-400';
}

const NotenPage: React.FC = () => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [avgs, setAvgs] = useState<AveragesResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [newSubject, setNewSubject] = useState('');
  const [newCategory, setNewCategory] = useState('schulaufgabe');
  const [newGrade, setNewGrade] = useState('');
  const [newWeight, setNewWeight] = useState('2.0');
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [newNotes, setNewNotes] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<Grade> & { grade?: string; weight?: string }>({});

  const load = useCallback(async () => {
    try {
      const [g, s, a] = await Promise.all([
        api<{ grades: Grade[] }>('/api/grades'),
        api<{ subjects: Subject[] }>('/api/subjects'),
        api<AveragesResponse>('/api/grades/averages'),
      ]);
      setGrades(g.grades); setSubjects(s.subjects); setAvgs(a);
      if (!newSubject && s.subjects.length > 0) setNewSubject(s.subjects[0].code);
    } catch (err: any) { setError(err?.message || 'Fehler'); }
  }, [newSubject]);

  useEffect(() => { load(); }, [load]);

  // Kategorie-Wechsel → Gewicht auf Default setzen
  const onCategoryChange = (key: string) => {
    setNewCategory(key);
    const cat = CATEGORIES.find(c => c.key === key);
    if (cat) setNewWeight(String(cat.defaultWeight));
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const gradeNum = parseFloat(newGrade.replace(',', '.'));
    if (!newSubject || !Number.isFinite(gradeNum)) {
      setError('Fach und Note sind erforderlich.'); return;
    }
    setBusy('create');
    try {
      await api('/api/grades', {
        method: 'POST',
        body: JSON.stringify({
          subject: newSubject,
          grade: gradeNum,
          weight: parseFloat(newWeight.replace(',', '.')) || 1,
          date: newDate || null,
          notes: newNotes || null,
          category: newCategory,
        }),
      });
      setNewGrade(''); setNewNotes('');
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const startEdit = (g: Grade) => {
    setEditingId(g.id);
    setEditDraft({
      grade: g.grade,
      weight: g.weight,
      date: g.date,
      notes: g.notes,
      category: g.category,
      subject: g.subject,
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setBusy(editingId);
    try {
      const body: Record<string, any> = {};
      if (editDraft.grade !== undefined) body.grade = parseFloat(String(editDraft.grade).replace(',', '.'));
      if (editDraft.weight !== undefined) body.weight = parseFloat(String(editDraft.weight).replace(',', '.'));
      if (editDraft.date !== undefined) body.date = editDraft.date || null;
      if (editDraft.notes !== undefined) body.notes = editDraft.notes || null;
      if (editDraft.category !== undefined) body.category = editDraft.category || null;
      if (editDraft.subject !== undefined) body.subject = editDraft.subject;
      await api(`/api/grades/${editingId}`, { method: 'PATCH', body: JSON.stringify(body) });
      setEditingId(null); setEditDraft({});
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const remove = async (g: Grade) => {
    if (!window.confirm('Note löschen?')) return;
    setBusy(g.id);
    try { await api(`/api/grades/${g.id}`, { method: 'DELETE' }); await load(); }
    catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const renderGradeInput = (value: string | undefined, onChange: (v: string) => void, placeholder = '') => (
    <input type="text" inputMode="decimal" value={value ?? ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm w-20" />
  );

  const categoryLabel = (key: string | null) => CATEGORIES.find(c => c.key === key)?.label || key || '';

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <GraduationCap className="w-6 h-6 text-green-400" />
        <h1 className="text-xl font-semibold">Noten</h1>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6 flex-1 w-full">
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Create */}
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h2 className="font-semibold mb-3">Neue Note eintragen</h2>
          <form onSubmit={create} className="grid grid-cols-2 sm:grid-cols-6 gap-3">
            <select value={newSubject} onChange={e => setNewSubject(e.target.value)} required
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm col-span-1">
              <option value="">Fach…</option>
              {subjects.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
            </select>
            <select value={newCategory} onChange={e => onCategoryChange(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm col-span-2"
              title="Art der Leistung — bestimmt die Gewichtung (änderbar)">
              {CATEGORIES.map(c => (
                <option key={c.key} value={c.key}>{c.label} ({c.defaultWeight}×)</option>
              ))}
            </select>
            <input type="text" inputMode="decimal" value={newGrade} onChange={e => setNewGrade(e.target.value)}
              placeholder="Note (1–6)" required
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm col-span-1" />
            <input type="text" inputMode="decimal" value={newWeight} onChange={e => setNewWeight(e.target.value)}
              placeholder="Gewicht"
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm col-span-1"
              title="Gewichtung — Default aus Kategorie, editierbar" />
            <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm col-span-1" />
            <input type="text" value={newNotes} onChange={e => setNewNotes(e.target.value)}
              placeholder="Notiz (optional, z. B. Thema)"
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm col-span-5" />
            <button type="submit" disabled={busy === 'create'}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-3 py-2 rounded-lg flex items-center justify-center gap-1 col-span-1">
              <Plus className="w-4 h-4" /> Anlegen
            </button>
          </form>
        </section>

        {/* Averages */}
        {avgs && (
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <h2 className="font-semibold mb-3">Durchschnitte</h2>
            {avgs.overall.average !== null ? (
              <p className="text-sm text-gray-400 mb-3">
                Gesamtschnitt: <span className={`text-xl font-bold ${gradeColor(avgs.overall.average)}`}>{avgs.overall.average.toFixed(2)}</span> aus {avgs.overall.count} Noten
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-3">Noch keine Noten.</p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {avgs.per_subject.map(s => (
                <div key={s.subject} className="bg-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-400">{s.subject}</div>
                  <div className={`text-xl font-bold ${s.average !== null ? gradeColor(s.average) : 'text-gray-500'}`}>
                    {s.average !== null ? s.average.toFixed(2) : '—'}
                  </div>
                  <div className="text-xs text-gray-500">{s.count} Noten · Gew. {parseFloat(String(s.total_weight || 0)).toFixed(1)}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* List */}
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h2 className="font-semibold mb-3">Alle Noten</h2>
          {grades.length === 0 ? (
            <p className="text-gray-500 text-sm">Noch keine Noten eingetragen.</p>
          ) : (
            <ul className="divide-y divide-gray-700">
              {grades.map(g => {
                const editing = editingId === g.id;
                const n = parseFloat(editing ? (editDraft.grade || g.grade) : g.grade);
                return (
                  <li key={g.id} className="py-3 flex items-center gap-3 text-sm flex-wrap">
                    <span className="bg-gray-700 px-2 py-1 rounded min-w-[56px] text-center text-xs">{g.subject}</span>

                    {editing ? (
                      renderGradeInput(editDraft.grade, v => setEditDraft({...editDraft, grade: v}), 'Note')
                    ) : (
                      <span className={`text-lg font-bold ${gradeColor(n)} min-w-[40px]`}>{Number.isFinite(n) ? n.toFixed(2) : '—'}</span>
                    )}

                    {editing ? (
                      <>
                        <select value={editDraft.category ?? ''} onChange={e => {
                          const cat = CATEGORIES.find(c => c.key === e.target.value);
                          setEditDraft({...editDraft, category: e.target.value || null, weight: cat ? String(cat.defaultWeight) : editDraft.weight });
                        }} className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs">
                          <option value="">— Kategorie —</option>
                          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label} ({c.defaultWeight}×)</option>)}
                        </select>
                        {renderGradeInput(editDraft.weight, v => setEditDraft({...editDraft, weight: v}), 'Gew.')}
                      </>
                    ) : (
                      <>
                        {g.category && <span className="bg-blue-500/20 text-blue-200 px-2 py-0.5 rounded text-xs">{categoryLabel(g.category)}</span>}
                        <span className="text-xs text-gray-400 min-w-[60px]">Gew. {parseFloat(g.weight).toFixed(1)}</span>
                      </>
                    )}

                    {editing ? (
                      <input type="date" value={editDraft.date ?? ''}
                        onChange={e => setEditDraft({...editDraft, date: e.target.value})}
                        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs" />
                    ) : (
                      <span className="text-xs text-gray-400 min-w-[90px]">{g.date ? new Date(g.date).toLocaleDateString('de-DE') : '—'}</span>
                    )}

                    {editing ? (
                      <input type="text" value={editDraft.notes ?? ''} onChange={e => setEditDraft({...editDraft, notes: e.target.value})}
                        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs flex-1 min-w-[120px]" placeholder="Notiz" />
                    ) : (
                      <span className="text-gray-300 flex-1 truncate min-w-[100px]">{g.notes ?? ''}</span>
                    )}

                    <div className="flex items-center gap-1">
                      {editing ? (
                        <>
                          <button onClick={saveEdit} disabled={busy === g.id} className="p-1.5 rounded bg-green-600 hover:bg-green-700 transition" title="Speichern">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={cancelEdit} className="p-1.5 rounded bg-gray-700 hover:bg-gray-600 transition" title="Abbrechen">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(g)} className="p-1.5 rounded bg-gray-700 hover:bg-gray-600 transition" title="Bearbeiten">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => remove(g)} disabled={busy === g.id} className="p-1.5 rounded bg-gray-700 hover:bg-red-600 transition" title="Löschen">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <AppFooter />
      </main>
    </div>
  );
};

export default NotenPage;
